import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { investigationService } from '../services/api';
import '../styles/ImportPanel.css';

// Common column name mappings for bank/exchange statements
const FIELD_MAPPINGS = {
  tx_hash:       ['hash', 'txhash', 'tx hash', 'transaction id', 'txid', 'transaction hash', 'id', 'reference', 'ref'],
  from_address:  ['from', 'from address', 'sender', 'source', 'debit account', 'from wallet'],
  to_address:    ['to', 'to address', 'recipient', 'destination', 'credit account', 'to wallet', 'receiver'],
  value:         ['value', 'amount', 'quantity', 'qty', 'sum', 'debit', 'credit', 'trx amount', 'token amount'],
  timestamp:     ['timestamp', 'date', 'time', 'datetime', 'date/time', 'block time', 'created at', 'created_at'],
  token_symbol:  ['token', 'symbol', 'currency', 'coin', 'asset', 'token symbol'],
  token_name:    ['token name', 'currency name', 'asset name'],
  block_number:  ['block', 'block number', 'block height', 'blockno'],
  status:        ['status', 'result', 'state'],
};

function autoMap(headers) {
  const mapping = {};
  headers.forEach(h => {
    const lower = h.toLowerCase().trim();
    for (const [field, aliases] of Object.entries(FIELD_MAPPINGS)) {
      if (!mapping[field] && aliases.some(a => lower === a || lower.includes(a))) {
        mapping[field] = h;
      }
    }
  });
  return mapping;
}

function parseRows(rows, mapping, blockchain, investigationAddress) {
  return rows
    .filter(row => Object.values(row).some(v => v !== '' && v !== null && v !== undefined))
    .map((row, i) => {
      const get = (field) => {
        const col = mapping[field];
        return col ? (row[col] ?? '') : '';
      };

      const rawValue = get('value');
      const value = parseFloat(String(rawValue).replace(/[^0-9.-]/g, '')) || 0;

      const rawTs = get('timestamp');
      let timestamp = null;
      if (rawTs !== '' && rawTs !== null && rawTs !== undefined) {
        const str = String(rawTs).trim();
        const num = Number(str);
        if (!isNaN(num) && str !== '') {
          if (num > 1e12) {
            timestamp = new Date(num).toISOString();
          } else if (num > 1e9) {
            timestamp = new Date(num * 1000).toISOString();
          } else if (num > 40000 && num < 60000) {
            const excelEpoch = new Date(1899, 11, 30);
            timestamp = new Date(excelEpoch.getTime() + num * 86400000).toISOString();
          } else {
            const d = new Date(str);
            timestamp = isNaN(d.getTime()) ? null : d.toISOString();
          }
        } else {
          const d = new Date(str);
          timestamp = isNaN(d.getTime()) ? null : d.toISOString();
        }
      }

      return {
        tx_hash:       get('tx_hash') || `IMPORT-${Date.now()}-${i}`,
        from_address:  get('from_address') || investigationAddress || 'N/A',
        to_address:    get('to_address') || 'N/A',
        value,
        gas_price:     0,
        block_number:  parseInt(get('block_number')) || 0,
        timestamp:     timestamp || new Date().toISOString(),
        token_name:    get('token_name') || get('token_symbol') || 'UNKNOWN',
        token_symbol:  get('token_symbol') || 'UNKNOWN',
        status:        get('status') || 'success',
        blockchain,
      };
    });
}

export default function ImportPanel({ investigationId, investigation, onImportComplete }) {
  const [step, setStep] = useState('upload');
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const blockchain   = investigation?.blockchain   || 'tron';
  const startAddress = investigation?.start_address || '';

  const handleFile = useCallback((file) => {
    if (!file) return;
    setError('');
    setFileName(file.name);
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'csv' || ext === 'txt') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data, meta }) => {
          setHeaders(meta.fields || []);
          setRows(data);
          setMapping(autoMap(meta.fields || []));
          setStep('map');
        },
        error: (e) => setError('Failed to parse CSV: ' + e.message),
      });
    } else if (['xlsx', 'xls', 'xlsm'].includes(ext)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb   = XLSX.read(e.target.result, { type: 'array' });
          const ws   = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
          const hdrs = data.length > 0 ? Object.keys(data[0]) : [];
          setHeaders(hdrs);
          setRows(data);
          setMapping(autoMap(hdrs));
          setStep('map');
        } catch (err) {
          setError('Failed to parse Excel file: ' + err.message);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setError('Unsupported file type. Please upload CSV or Excel (.xlsx, .xls).');
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handlePreview = () => {
    const parsed = parseRows(rows.slice(0, 5), mapping, blockchain, startAddress);
    setPreview(parsed);
    setStep('preview');
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    try {
      const allTx    = parseRows(rows, mapping, blockchain, startAddress);
      const response = await investigationService.importTransactions(investigationId, allTx);
      setImportResult(response.data);
      setStep('done');
      onImportComplete?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFileName('');
    setHeaders([]);
    setRows([]);
    setMapping({});
    setPreview([]);
    setImportResult(null);
    setError('');
  };

  return (
    <div className="import-panel">
      <div className="import-header">
        <h3>📂 Import Statement</h3>
        <p className="import-subtitle">Import transactions from CSV or Excel bank/exchange statements</p>
      </div>

      <div className="step-indicator">
        {['upload', 'map', 'preview', 'done'].map((s, i) => (
          <div key={s} className={`step ${step === s ? 'active' : ''} ${['upload','map','preview','done'].indexOf(step) > i ? 'done' : ''}`}>
            <div className="step-dot">{['upload','map','preview','done'].indexOf(step) > i ? '✓' : i + 1}</div>
            <span>{['Upload', 'Map Columns', 'Preview', 'Done'][i]}</span>
          </div>
        ))}
      </div>

      {error && <div className="import-error">⚠️ {error}</div>}

      {step === 'upload' && (
        <div
          className={`drop-zone ${dragging ? 'dragging' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,.xlsm,.txt"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
          <div className="drop-icon">📄</div>
          <p className="drop-title">Drop your statement here</p>
          <p className="drop-sub">or click to browse</p>
          <div className="supported-formats">
            <span className="format-badge">CSV</span>
            <span className="format-badge">XLSX</span>
            <span className="format-badge">XLS</span>
          </div>
          <p className="drop-hint">Supports Binance, Kraken, Coinbase, TronScan, Etherscan exports and bank statements</p>
        </div>
      )}

      {step === 'map' && (
        <div className="mapping-step">
          <div className="file-info">
            <span className="file-icon">📄</span>
            <strong>{fileName}</strong>
            <span className="row-count">{rows.length} rows found</span>
          </div>
          <p className="mapping-desc">We auto-detected the columns below. Review and adjust if needed:</p>
          <div className="mapping-grid">
            {Object.keys(FIELD_MAPPINGS).map(field => (
              <div key={field} className="mapping-row">
                <label className="field-label">
                  {field === 'tx_hash' ? 'TX Hash' :
                   field === 'from_address' ? 'From Address' :
                   field === 'to_address' ? 'To Address' :
                   field === 'value' ? 'Value / Amount' :
                   field === 'timestamp' ? 'Date / Timestamp' :
                   field === 'token_symbol' ? 'Token Symbol' :
                   field === 'token_name' ? 'Token Name' :
                   field === 'block_number' ? 'Block Number' : 'Status'}
                  {['tx_hash','value','timestamp'].includes(field) && <span className="required">*</span>}
                </label>
                <select
                  value={mapping[field] || ''}
                  onChange={(e) => setMapping({ ...mapping, [field]: e.target.value || undefined })}
                  className={mapping[field] ? 'mapped' : ''}
                >
                  <option value="">— not mapped —</option>
                  {headers.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                {mapping[field] && (
                  <span className="sample-value">
                    {String(rows[0]?.[mapping[field]] ?? '').slice(0, 30)}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="mapping-actions">
            <button className="btn-secondary" onClick={reset}>← Back</button>
            <button
              className="btn-primary"
              onClick={handlePreview}
              disabled={!mapping.value && !mapping.timestamp}
            >
              Preview Import →
            </button>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div className="preview-step">
          <div className="preview-summary">
            <div className="summary-stat"><strong>{rows.length}</strong><span>Total rows</span></div>
            <div className="summary-stat"><strong>{blockchain.toUpperCase()}</strong><span>Blockchain</span></div>
            <div className="summary-stat"><strong>{preview.filter(t => t.value > 0).length}/5</strong><span>With values</span></div>
          </div>
          <p className="preview-desc">Showing first 5 rows of {rows.length} to be imported:</p>
          <div className="preview-table-wrap">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>TX Hash</th><th>From</th><th>To</th>
                  <th>Value</th><th>Token</th><th>Date</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((tx, i) => (
                  <tr key={i}>
                    <td className="hash-cell" title={tx.tx_hash}>{tx.tx_hash.slice(0, 16)}...</td>
                    <td className="addr-cell" title={tx.from_address}>{tx.from_address.slice(0, 14)}...</td>
                    <td className="addr-cell" title={tx.to_address}>{tx.to_address.slice(0, 14)}...</td>
                    <td className="value-cell">{tx.value.toFixed(4)}</td>
                    <td>{tx.token_symbol}</td>
                    <td>{tx.timestamp ? new Date(tx.timestamp).toLocaleDateString() : '—'}</td>
                    <td><span className={`status-pill ${tx.status}`}>{tx.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mapping-actions">
            <button className="btn-secondary" onClick={() => setStep('map')}>← Edit Mapping</button>
            <button className="btn-success" onClick={handleImport} disabled={importing}>
              {importing ? '⏳ Importing...' : `✅ Import ${rows.length} Transactions`}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="done-step">
          <div className="done-icon">✅</div>
          <h4>Import Complete!</h4>
          <div className="done-stats">
            <div className="done-stat"><strong>{importResult?.imported || 0}</strong><span>Imported</span></div>
            <div className="done-stat"><strong>{importResult?.skipped || 0}</strong><span>Skipped (duplicates)</span></div>
            <div className="done-stat"><strong>{importResult?.errors || 0}</strong><span>Errors</span></div>
          </div>
          {importResult?.message && <p className="done-message">{importResult.message}</p>}
          <button className="btn-primary" onClick={reset}>Import Another File</button>
        </div>
      )}
    </div>
  );
}
